import { useState } from 'react';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import type { DerbyState } from '@/entities/derby';
import { RADIUS, StaticSquircle } from '@/shared/ui';

import styles from './DerbyResult.module.scss';

/** Размер кольца и толщина обода — те же, что у CSS-варианта в `.ring`. */
const RING_SIZE = 200;
const RING_STROKE = 18;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/**
 * Кольцо-таймер (узел 393:6281/393:6287).
 *
 * Без `progress` — сплошной CSS-круг, как в ките. С `progress` (доля
 * оставшегося времени, 0…1) розовый обод убывает по часовой стрелке
 * от двенадцати часов, открывая серую дорожку (правка 14.09.2026):
 * игрок видит, сколько времени осталось, не вчитываясь в цифры.
 */
export function Ring({
  color,
  progress,
  children,
}: {
  color: 'pink' | 'white';
  progress?: number;
  children: ReactNode;
}) {
  if (progress === undefined) {
    return (
      <div
        className={clsx(
          styles.ring,
          color === 'pink' ? styles.ringPink : styles.ringWhite,
        )}
      >
        <span className={styles.ringText}>{children}</span>
      </div>
    );
  }

  const left = Math.min(1, Math.max(0, progress));
  return (
    <div
      className={clsx(styles.ring, styles.ringProgress)}
      data-testid="derby-timer-ring"
    >
      <svg
        className={styles.ringSvg}
        viewBox={`0 0 ${String(RING_SIZE)} ${String(RING_SIZE)}`}
        aria-hidden
      >
        <circle
          className={styles.ringTrack}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
        />
        <circle
          className={clsx(
            styles.ringArc,
            color === 'pink' ? styles.ringArcPink : styles.ringArcWhite,
          )}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - left)}
          data-progress={left.toFixed(3)}
        />
      </svg>
      <span className={styles.ringText}>{children}</span>
    </div>
  );
}

/**
 * Верх экрана итога Дерби — розовая клякса-подложка (756:6432) и иллюстрация
 * «секундомер» (756:6428), вместо статичного кольца 00:00:00. Одна связка
 * для модалки и экрана /derby.
 *
 * Числа один в один с макетом (узел 725:6055, 358×301):
 * - `.art` резервирует 56px сверху под строку крестика (в модалке там
 *   абсолютом крестик, на /derby — просто розовый фон от кляксы) и режет
 *   всё лишнее `overflow: hidden`, ровно как клипающий фрейм в Figma;
 * - `.shapeBg` — тот же бокс 735.778 со смещением -282/-312, внутри шейп
 *   498.51×542.037, повёрнутый на 135° и отражённый по Y;
 * - `.imageBox` (287×245) кадрирует PNG теми же процентами, что в макете.
 */
export function DerbyResultArt() {
  return (
    <div className={styles.art} aria-hidden>
      <div className={styles.shapeBg}>
        <img className={styles.shapeBgImage} src="/images/derby/shape-bg.svg" alt="" />
      </div>
      <div className={styles.imageBox}>
        <img className={styles.image} src="/images/derby/result-art.png" alt="" />
      </div>
    </div>
  );
}

/**
 * Карточка с числом верных ответов и начисленными очками, без кольца.
 *
 * «Ответы: N из M Правильно» — аккордеон (узлы 725:5951 свёрнут / 725:6120
 * развёрнут): разворачивает статистику по каждому вопросу — правильность
 * и факт по теме. Очков за отдельный вопрос сервер не отдаёт (награда идёт
 * одной суммой, ГДД §6.3), поэтому число рядом с верным ответом — это
 * итоговая награда, поровну разделённая на число верных ответов, а не
 * настоящая цена конкретного вопроса.
 */
export function DerbyResultCard({ derby }: { derby: DerbyState }) {
  const [isOpen, setIsOpen] = useState(false);
  const pointsPerCorrect =
    derby.correctCount > 0
      ? Math.floor((derby.pointsAwarded ?? 0) / derby.correctCount)
      : 0;

  return (
    <div className={styles.resultCard}>
      <p className={styles.resultTitle}>Результат</p>

      <button
        type="button"
        className={styles.resultRow}
        aria-expanded={isOpen}
        data-testid="derby-stats-toggle"
        onClick={() => {
          setIsOpen((v) => !v);
        }}
      >
        <span className={styles.resultLabel}>Ответы:</span>
        <span className={styles.resultToggleValue}>
          <span className={styles.resultLabel}>
            {derby.correctCount} из {derby.questions.length} Правильно
          </span>
          <StaticSquircle asChild width={24} height={24} cornerRadius={RADIUS.sm}>
            <span className={styles.chevronBox}>
              <img
                // Та же логика, что у FaqList: chevron-closed.svg смотрит
                // вверх и означает «развёрнуто», chevron-open.svg — вниз
                // и означает «свёрнуто, можно раскрыть»
                src={
                  isOpen
                    ? '/images/icons/chevron-closed.svg'
                    : '/images/icons/chevron-open.svg'
                }
                alt=""
                aria-hidden
                className={styles.chevron}
              />
            </span>
          </StaticSquircle>
        </span>
      </button>

      {isOpen ? (
        <div className={styles.factList} data-testid="derby-stats-facts">
          <div className={styles.factRow}>
            <span className={styles.factIndicator} aria-hidden />
            <p className={styles.factListLabel}>Фанфакт</p>
          </div>
          {derby.questions.map((q) => (
            <div className={styles.factRow} key={q.id}>
              <span className={styles.factIndicator}>
                {q.isCorrect ? (
                  <span className={styles.factCorrect}>
                    <img className={styles.totalIcon} src="/images/header/fire.svg" alt="" aria-hidden />
                    {pointsPerCorrect}
                  </span>
                ) : (
                  <img className={styles.factIncorrectIcon} src="/images/icons/incorrect.svg" alt="" aria-hidden />
                )}
              </span>
              <p className={styles.factText}>{q.factText}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className={clsx(styles.resultRow, styles.resultRowLast)}>
        <span className={styles.resultLabel}>Итого</span>
        <span className={styles.totalPoints} data-testid="derby-points">
          <img className={styles.totalIcon} src="/images/header/fire.svg" alt="" aria-hidden />
          {derby.pointsAwarded ?? 0}
        </span>
      </div>
    </div>
  );
}

/**
 * Итог пройденного Дерби (узел 438:51717 «Результат») — иллюстрация
 * «секундомер» и карточка результата. Для страницы /derby (только что прошёл
 * квиз) — там нет крестика и группировки модалки, поэтому картинка и карточка
 * здесь просто идут подряд. Модалка из промо-карточки (см. DerbyResultModal)
 * группирует их иначе (картинка с крестиком и розовой кляксой сверху, карточка
 * с кнопкой снизу) и собирает DerbyResultArt/DerbyResultCard сама.
 */
export function DerbyResultBody({ derby }: { derby: DerbyState }) {
  return (
    <>
      <DerbyResultArt />
      <DerbyResultCard derby={derby} />
    </>
  );
}
